/**
 * Decodes a deal ID from a URL path param. Deal IDs contain # so they must be
 * encoded (encodeURIComponent) when building the path and decoded when reading.
 */
export function decodeDealIdFromParam(param: string | undefined): string | undefined {
  if (param == null || param === '') return undefined;
  try {
    return decodeURIComponent(param);
  } catch {
    return param;
  }
}

/**
 * Deal IDs must be composite: OFFERING#<offeringID>#BUYER#<buyerCompanyID>.
 * Returns false for non-composite values (e.g. "OFFERING" or a listing id) so we don't call APIs that expect this format.
 */
export function isValidDealId(dealId: string | null | undefined): boolean {
  if (!dealId || typeof dealId !== 'string') return false;
  const parts = dealId.split('#');
  if (parts.length !== 4) return false;
  const [offeringPrefix, offeringID, buyerPrefix, buyerCompanyID] = parts;
  return (
    offeringPrefix === 'OFFERING' &&
    buyerPrefix === 'BUYER' &&
    Boolean(offeringID && buyerCompanyID)
  );
}

/** Extracts offering ID from composite deal ID (OFFERING#<id>#BUYER#...). Returns undefined if invalid. */
export function getOfferingIdFromDealId(dealId: string | null | undefined): string | undefined {
  if (!dealId || !isValidDealId(dealId)) return undefined;
  return dealId.split('#')[1];
}

/** Extracts buyer company ID from composite deal ID (OFFERING#...#BUYER#<id>). Returns undefined if invalid. */
export function getBuyerCompanyIdFromDealId(dealId: string | null | undefined): string | undefined {
  if (!dealId || !isValidDealId(dealId)) return undefined;
  return dealId.split('#')[3];
}

/** Coerces null/undefined/NaN to 0. Shared by monthly-table mappers. */
export function toNum(v: number | null | undefined): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Formats cents as USD currency string (e.g. 150000 -> "$1,500.00"). */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Helper function to format percentage
export const formatPercentage = (value: number): string => {
  return `${value.toFixed(3)}%`;
};

// Helper function to format date
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * Formats a payment month string (YYYY-MM) into a human-readable abbreviated
 * label (e.g. "2026-02" → "Feb 2026"). Returns null when the input is falsy
 * or not a recognised YYYY-MM value.
 */
export function formatPaymentMonth(paymentMonth: string | null | undefined): string | null {
  if (!paymentMonth) return null;
  const [year, month] = paymentMonth.split('-');
  if (!year || !month) return null;
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}
